'use client';

import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { ComponentProps, useCallback, useContext } from 'react';
import { useWatch, type Control } from 'react-hook-form';
import { CartContext } from '@/entities/cart';
import type { CartFormValues } from '@/entities/cart/model/cart-context';
import { AppConfigContext } from '@/features/app-providers/app-config-provider';
import { DeliveryType } from '@/features/enums/DeliveryType';
import { QueryApi } from '@/features/query/api';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import { Icon } from '@/shared/ui/icon';
import { Layout } from '@/shared/ui/layout';
import { ModalService } from '@/shared/ui/modal/ModalService';
import { VenuesQuery } from '../../api/queries/VenuesQuery';
import { useIsVenueRouterPage } from '../../hooks/useIsVenueRouterPage';
import './styles.scss';

const SelectVenueAddress = ({
    isDelivery,
    street,
    city,
    venueName,
}: {
    isDelivery: boolean;
    street?: string;
    city?: string;
    venueName?: string;
}) => {
    const t = useTranslations();
    return (
        <span className="select-venue__label">
            <span className="select-venue__subtitle">
                {isDelivery ? t('SELECT_VENUE.DELIVER_TO') : t('SELECT_VENUE.DELIVER_FROM')}
            </span>
            <span className="select-venue__name">
                {isDelivery ? `${(street?.length ?? 0) < 6 ? city : ''}${street}` : venueName}
            </span>
        </span>
    );
};

const SelectVenueWatchedAddress = ({
    control,
    venueName,
}: {
    control: Control<CartFormValues>;
    venueName?: string;
}) => {
    const cartAddress = useWatch({ control, name: 'deliveryMethod.address' });
    const deliveryType = useWatch({ control, name: 'deliveryMethod.type' });
    const isDelivery = !!cartAddress?.street && deliveryType === DeliveryType.DELIVERY;
    return (
        <SelectVenueAddress
            isDelivery={isDelivery}
            street={cartAddress?.street}
            city={cartAddress?.city}
            venueName={venueName}
        />
    );
};

type Props = {
    variant?: 'default' | 'compact' | 'menu';
    isImageVisible?: boolean;
    areSearchFieldsVisible?: boolean;
    isSignInVisible?: boolean;
    'data-testid'?: string;
    className?: string;
} & ComponentProps<typeof Layout>;

export const SelectVenue = ({
    variant = 'default',
    isImageVisible,
    areSearchFieldsVisible,
    isSignInVisible,
    className,
    children,
    ...props
}: Props) => {
    const t = useTranslations();
    const isVenueRouterPage = useIsVenueRouterPage();
    const { venueRouter } = useContext(AppConfigContext);
    const venueQuery = useQuery(QueryApi.Venue.queries.Venue);
    const venuesQuery = useQuery(VenuesQuery);
    const { formMethods } = useContext(CartContext);
    const cartControl = formMethods?.control;

    const onOpen = useCallback(() => {
        ModalService.showPrefab('SelectVenueModal', {
            isImageVisible,
            areSearchFieldsVisible,
            isSignInVisible,
            allowSelectCurrent: isVenueRouterPage,
        });
    }, [areSearchFieldsVisible, isImageVisible, isSignInVisible, isVenueRouterPage]);

    if ((!!venueRouter && !venueRouter.showRouter) || (venuesQuery.data ?? []).length <= 1) {
        return null;
    }

    if (variant === 'menu') {
        return (
            <Layout
                element={Button}
                className={className}
                onClick={onOpen}
                data-testid={props['data-testid'] ?? 'select-venue-menu'}
                {...props}
            >
                {children}
            </Layout>
        );
    }

    return (
        <Layout
            element="button"
            type="button"
            className={cn('select-venue', variant === 'compact' && 'select-venue--compact', className)}
            onClick={onOpen}
            data-testid={props['data-testid'] ?? 'select-venue'}
            {...props}
        >
            {variant === 'default' ? (
                cartControl ? (
                    <SelectVenueWatchedAddress control={cartControl} venueName={venueQuery.data?.name} />
                ) : (
                    <SelectVenueAddress isDelivery={false} venueName={venueQuery.data?.name} />
                )
            ) : (
                <span className="select-venue__name">{t('SELECT_VENUE.LABEL')}</span>
            )}
            <Icon name="chevron-right" className="select-venue__chevron" />
        </Layout>
    );
};
